import React, { useState } from 'react';
import { chairman, distributorBenefits, contactInfo } from '../mock';
import { MapPin, Phone, Mail, Globe, Check, ArrowUpRight, Send, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { Reveal } from './Reveal';
import { motion } from 'framer-motion';
import DistributorDialog from './DistributorDialog';
import { submitEnquiry, enquiryError } from '../lib/enquiries';

const EMPTY_FORM = { name: '', email: '', company: '', message: '' };

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [distributorOpen, setDistributorOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast({
        title: 'Missing details',
        description: 'Please provide your name and email.',
        variant: 'destructive',
      });
      return;
    }
    setSending(true);
    try {
      await submitEnquiry('contact', form);
      toast({
        title: 'Enquiry received',
        description: `Thank you ${form.name.trim().split(' ')[0]}, our team will reach out shortly.`,
      });
      setForm(EMPTY_FORM);
    } catch (err) {
      toast({ title: 'Could not send', description: enquiryError(err), variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const handleDistributor = () => setDistributorOpen(true);

  return (
    <section id="distributor" className="relative py-24 lg:py-32 bg-[#fafaf7] overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-16">
        {/* Section heading */}
        <Reveal>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-10 h-[1px] bg-[#c4a24a]" />
            <span className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] uppercase">
              07 — Partner With Us
            </span>
          </div>
          <div className="grid lg:grid-cols-12 gap-6 items-end mb-16">
            <h2
              className="lg:col-span-8 text-[42px] lg:text-[60px] xl:text-[68px] leading-[1.03] font-medium text-[#0f2c6b] text-balance"
              style={{ fontFamily: 'Fraunces, serif', letterSpacing: '-0.03em' }}
            >
              Let’s <span className="italic text-[#c4a24a] font-light">connect.</span>
            </h2>
            <p className="lg:col-span-4 text-[14.5px] leading-[1.8] text-slate-600 font-light">
              Whether you’re interested in becoming a distributor, sourcing bulk supplies, or exploring corporate partnerships — we’d love to hear from you.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-8">
          {/* Chairman */}
          <Reveal delay={0.1}>
            <div className="bg-white p-8 lg:p-10 h-full relative overflow-hidden group">
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#c4a24a] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
              <div className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] mb-6 uppercase">
                Message from the Chairman
              </div>
              <div className="flex flex-col gap-5">
                <div className="flex-1">
                  <div
                    className="text-[42px] leading-none text-[#c4a24a] font-medium mb-1"
                    style={{ fontFamily: 'Fraunces, serif' }}
                  >
                    “
                  </div>
                  <p
                    className="text-[14px] leading-[1.7] text-slate-700 font-light italic -mt-4"
                    style={{ fontFamily: 'Fraunces, serif' }}
                  >
                    At Tropic Blue®, our mission is simple — to deliver pure, safe and premium hydration while continuously innovating in packaging, quality and customer satisfaction. Every bottle reflects our commitment to excellence and trust.
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-100">
                <div
                  className="text-[16px] font-medium text-[#0f2c6b]"
                  style={{ fontFamily: 'Fraunces, serif' }}
                >
                  {chairman.name}
                </div>
                <div className="text-[10.5px] text-slate-500 tracking-[0.2em] uppercase mt-1">
                  {chairman.title} · {chairman.company}
                </div>
              </div>
            </div>
          </Reveal>

          {/* Distributor */}
          <Reveal delay={0.2}>
            <div className="bg-white p-8 lg:p-10 h-full relative overflow-hidden group">
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#c4a24a] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
              <div className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] mb-6 uppercase">
                Grow With Tropic Blue®
              </div>
              <h3
                className="text-[26px] leading-[1.15] font-medium text-[#0f2c6b] mb-4"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                Partner with one of India’s <span className="italic text-[#c4a24a] font-light">emerging</span> premium water brands.
              </h3>
              <ul className="space-y-3 mb-8 mt-6">
                {distributorBenefits.map((b, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-[13.5px] text-slate-700 font-light">
                    <Check size={14} className="text-[#c4a24a] mt-1 shrink-0" strokeWidth={2.5} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={handleDistributor}
                data-testid="become-distributor-button"
                className="premium-button group/btn w-full bg-[#0f2c6b] hover:bg-[#1e3a8a] text-white text-[11.5px] font-medium tracking-[0.22em] px-6 py-4 flex items-center justify-center gap-3 transition-all duration-300"
              >
                BECOME A DISTRIBUTOR
                <ArrowUpRight
                  size={14}
                  className="group-hover/btn:rotate-45 transition-transform duration-300"
                />
              </button>
            </div>
          </Reveal>

          {/* Let's Connect */}
          <Reveal delay={0.3}>
            <div
              id="contact"
              className="bg-gradient-to-br from-[#0a1f4a] via-[#0f2c6b] to-[#0a1f4a] text-white p-8 lg:p-10 h-full relative overflow-hidden group"
            >
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#c4a24a] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700" />
              <div className="absolute -bottom-20 -right-20 w-56 h-56 bg-[#c4a24a]/[0.08] rounded-full blur-2xl" />

              <div className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] mb-6 uppercase">
                Let’s Connect
              </div>
              <div
                className="text-[30px] font-medium text-white mb-1"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                Tropic <span className="italic text-[#c4a24a] font-light">Blue</span>®
              </div>
              <div className="text-[10.5px] text-white/60 tracking-[0.28em] uppercase mb-8">
                Premium Packaged Drinking Water
              </div>

              <div className="space-y-5 text-[13.5px] text-white/85 font-light">
                <div className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center shrink-0">
                    <MapPin size={13} className="text-[#c4a24a]" />
                  </div>
                  <div>
                    <div className="text-[9.5px] tracking-[0.28em] text-white/50 mb-1 uppercase">
                      Corporate Office
                    </div>
                    <div className="whitespace-pre-line leading-[1.6]">{contactInfo.address}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center shrink-0">
                    <Phone size={13} className="text-[#c4a24a]" />
                  </div>
                  <a
                    href={`tel:${contactInfo.phone}`}
                    className="hover:text-[#c4a24a] transition-colors"
                  >
                    {contactInfo.phone}
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center shrink-0">
                    <Mail size={13} className="text-[#c4a24a]" />
                  </div>
                  <a
                    href={`mailto:${contactInfo.email}`}
                    className="hover:text-[#c4a24a] transition-colors"
                  >
                    {contactInfo.email}
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center shrink-0">
                    <Globe size={13} className="text-[#c4a24a]" />
                  </div>
                  <a
                    href={`https://${contactInfo.website}`}
                    className="hover:text-[#c4a24a] transition-colors"
                  >
                    {contactInfo.website}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Enquiry form */}
        <Reveal delay={0.2}>
          <div className="bg-white p-8 lg:p-12 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#c4a24a] via-transparent to-[#c4a24a]/60" />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              <div className="lg:col-span-5">
                <div className="text-[10px] font-medium tracking-[0.35em] text-[#c4a24a] mb-4 uppercase">
                  Quick Enquiry
                </div>
                <h3
                  className="text-[32px] lg:text-[40px] font-medium text-[#0f2c6b] leading-[1.1] mb-4"
                  style={{ fontFamily: 'Fraunces, serif', letterSpacing: '-0.02em' }}
                >
                  Have <span className="italic text-[#c4a24a] font-light">questions?</span> We’d love to hear from you.
                </h3>
                <p className="text-[14px] text-slate-600 leading-[1.75] font-light mb-6">
                  Fill out the form and our team will get back to you within one business day.
                </p>
                <div className="flex items-center gap-3 text-[12px] text-slate-500 font-light">
                  <div className="w-8 h-[1px] bg-[#c4a24a]" />
                  <span className="tracking-wider">Response within 24 hours</span>
                </div>
              </div>
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5"
              >
                <InputField
                  testId="contact-name-input"
                  label="Your Name"
                  value={form.name}
                  onChange={(v) => setForm({ ...form, name: v })}
                />
                <InputField
                  testId="contact-email-input"
                  label="Email Address"
                  type="email"
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                />
                <InputField
                  testId="contact-company-input"
                  className="sm:col-span-2"
                  label="Company (optional)"
                  value={form.company}
                  onChange={(v) => setForm({ ...form, company: v })}
                />
                <TextareaField
                  testId="contact-message-input"
                  className="sm:col-span-2"
                  label="Your Message"
                  value={form.message}
                  onChange={(v) => setForm({ ...form, message: v })}
                />
                <div className="sm:col-span-2 flex justify-end">
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit-button"
                    className="premium-button group bg-[#0f2c6b] hover:bg-[#1e3a8a] disabled:opacity-70 text-white text-[11.5px] font-medium tracking-[0.22em] px-8 py-4 flex items-center gap-3 transition-all duration-300"
                  >
                    {sending ? 'SENDING' : 'SEND ENQUIRY'}
                    {sending ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Send
                        size={14}
                        className="group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform duration-300"
                      />
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Reveal>
      </div>
      <DistributorDialog open={distributorOpen} onOpenChange={setDistributorOpen} />
    </section>
  );
};

const InputField = ({ label, value, onChange, type = 'text', className = '', testId }) => {
  const [focused, setFocused] = useState(false);
  const active = focused || value;

  return (
    <div className={`relative ${className}`}>
      <label
        className={`absolute left-0 pointer-events-none transition-all duration-300 ${
          active
            ? 'top-0 text-[10px] tracking-[0.25em] text-[#c4a24a] uppercase'
            : 'top-4 text-[13px] text-slate-400'
        }`}
      >
        {label}
      </label>
      <input
        type={type}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="w-full bg-transparent border-b border-slate-300 focus:border-[#0f2c6b] pt-5 pb-2 text-[14px] text-slate-800 transition-colors duration-300"
      />
      <motion.div
        className="absolute bottom-0 left-0 h-[2px] bg-[#c4a24a] origin-left"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: focused ? 1 : 0 }}
        transition={{ duration: 0.4 }}
        style={{ width: '100%' }}
      />
    </div>
  );
};

const TextareaField = ({ label, value, onChange, className = '', testId }) => {
  const [focused, setFocused] = useState(false);
  const active = focused || value;

  return (
    <div className={`relative ${className}`}>
      <label
        className={`absolute left-0 pointer-events-none transition-all duration-300 ${
          active
            ? 'top-0 text-[10px] tracking-[0.25em] text-[#c4a24a] uppercase'
            : 'top-4 text-[13px] text-slate-400'
        }`}
      >
        {label}
      </label>
      <textarea
        rows={3}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="w-full bg-transparent border-b border-slate-300 focus:border-[#0f2c6b] pt-5 pb-2 text-[14px] text-slate-800 transition-colors duration-300 resize-none"
      />
      <motion.div
        className="absolute bottom-0 left-0 h-[2px] bg-[#c4a24a] origin-left"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: focused ? 1 : 0 }}
        transition={{ duration: 0.4 }}
        style={{ width: '100%' }}
      />
    </div>
  );
};

export default Contact;
